import { useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertTriangle, XCircle, Play, Eye, MoreHorizontal } from "lucide-react";
import StoreDetailsModal from "../StoreDetailsModal";
import WindowWorldLogo from "../WindowWorldLogo";
import useStoreValidation from "../../hooks/useStoreValidation.js";
import ValidationProgressBar from "./ValidationProgressBar.jsx";
import ValidationQueueStatus from "./ValidationQueueStatus.jsx";
import DomainOverview from "./DomainOverview.jsx";
import { getStoreStatus } from "../../utils/statusUtils.js";

export default function StoreCard({ store, onValidationComplete, isExternallyValidating = false, externalValidationProgress = null }) {
    const [showDetailedResults, setShowDetailedResults] = useState(false);

    // Use the validation hook
    const {
        validationResults,
        setValidationResults,
        isValidating,
        validationError,
        isQueued,
        queuePosition,
        validationProgress,
        runValidation
    } = useStoreValidation(store, onValidationComplete);

    // Track if we're being validated externally
    const isActuallyValidating = isValidating || isExternallyValidating;

    // Use external progress if available, otherwise use internal progress
    const displayProgress = externalValidationProgress || validationProgress;

    // Check if store has 0 pages
    const hasNoPages = store.pages.length === 0;

    // Determine overall store status
    const overallStatus = getStoreStatus({ validationResults });

    const statusConfig = {
        clean: {
            icon: <CheckCircle2 size={14} />,
            className: 'bg-emerald-50/50 text-emerald-600 border-emerald-200/30',
            label: 'Healthy',
            dotColor: 'bg-emerald-500'
        },
        error: {
            icon: <XCircle size={14} />,
            className: 'bg-red-50/50 text-red-600 border-red-200/30',
            label: 'Issues',
            dotColor: 'bg-red-500'
        },
        pending: {
            icon: <AlertTriangle size={14} />,
            className: 'bg-amber-50/50 text-amber-600 border-amber-200/30',
            label: 'Changes Required',
            dotColor: 'bg-amber-500'
        },
        review: {
            icon: <AlertTriangle size={14} />,
            className: 'bg-blue-50/50 text-blue-600 border-blue-200/30',
            label: 'Review',
            dotColor: 'bg-blue-500'
        }
    };

    const currentStatus = statusConfig[overallStatus];

    return (
        <div className={`card group transition-all duration-200 ${hasNoPages ? 'opacity-50 grayscale cursor-not-allowed' : 'hover:shadow-lg'}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-5">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 p-2 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <WindowWorldLogo size={36} className="drop-shadow-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                                <h3 className="text-lg font-extrabold text-slate-900 line-clamp-1 hover:line-clamp-none transition-all duration-200"
                                    title={store.name}>
                                    {store.name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <p className="text-sm font-semibold text-slate-700">
                                        {store.region}
                                    </p>
                                    <span className={`inline-block w-1 h-1 rounded-full ${currentStatus.dotColor}`}></span>
                                    <p className={`text-xs font-medium ${hasNoPages ? 'text-red-600 font-bold' : 'text-slate-500'}`}>
                                        {store.pages.length} page{store.pages.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                {store.address && (
                                    <p className="text-xs font-normal text-slate-500 mt-1.5 line-clamp-1 hover:line-clamp-none transition-all duration-200"
                                        title={store.address}>
                                        {store.address}
                                    </p>
                                )}
                            </div>
                            {/* Status indicator */}
                            <div className={`px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 ${currentStatus.className} transition-all duration-200`}>
                                {currentStatus.icon}
                                <span className="hidden sm:inline">{currentStatus.label}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Domain Overview */}
            <DomainOverview pages={store.pages} hasNoPages={hasNoPages} />

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            runValidation();
                        }}
                        disabled={isActuallyValidating || isQueued || hasNoPages}
                        className={`
              flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold
              transition-all duration-200 border shadow-sm
              ${isActuallyValidating || hasNoPages
                                ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                : isQueued
                                    ? 'bg-blue-50 text-blue-600 border-blue-200 cursor-not-allowed opacity-75'
                                    : 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 border-orange-200 hover:from-orange-100 hover:to-orange-150 hover:shadow-md hover:scale-105 active:scale-100'
                            }
            `}
                    >
                        {isActuallyValidating || isQueued ? (
                            <ValidationQueueStatus
                                isValidating={isActuallyValidating}
                                isQueued={isQueued}
                                queuePosition={queuePosition}
                            />
                        ) : (
                            <>
                                <Play size={12} />
                                Validate
                            </>
                        )}
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDetailedResults(true);
                        }}
                        disabled={!validationResults && !validationError || hasNoPages}
                        className={`
              flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all duration-200 text-xs font-semibold border shadow-sm
              ${validationResults || validationError
                                ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-blue-200 hover:from-blue-100 hover:to-blue-150 hover:shadow-md hover:scale-105 active:scale-100'
                                : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                            }
            `}
                    >
                        <Eye size={12} />
                        <span className="hidden sm:inline">Details</span>
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all duration-200"
                    >
                        <MoreHorizontal size={16} />
                    </button>
                </div>

                {/* Last validation info with staleness indicator */}
                <div className="text-xs font-normal">
                    {validationResults ? (
                        <div className="flex items-center gap-1.5">
                            {validationResults.daysOld !== null && validationResults.daysOld !== undefined ? (
                                <>
                                    {/* Show staleness warning */}
                                    {validationResults.isStale ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                                            <AlertTriangle size={12} />
                                            <span title={`Last checked ${validationResults.daysOld} days ago`}>
                                                {validationResults.daysOld}d ago
                                            </span>
                                        </span>
                                    ) : validationResults.daysOld >= 6 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50/50 text-amber-600 border border-amber-100 font-medium">
                                            <span title={`Last checked ${validationResults.daysOld} days ago`}>
                                                {validationResults.daysOld}d ago
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-slate-500" title={`Last checked ${validationResults.daysOld} days ago`}>
                                            {validationResults.daysOld}d ago
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="text-slate-500" title={new Date(validationResults.validationDate || validationResults.timestamp || Date.now()).toLocaleString()}>
                                    Updated {new Date(validationResults.validationDate || validationResults.timestamp || Date.now()).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-slate-500">Not validated</span>
                    )}
                </div>
            </div>

            {/* Progress Bar */}
            <ValidationProgressBar
                progress={displayProgress}
                isActive={isActuallyValidating && (isValidating ? validationProgress.isActive : true)}
            />

            {/* Validation Error Summary */}
            {validationError && (
                <div className="mt-3 bg-red-50/50 border border-red-200/30 rounded-lg p-3 animate-fadeIn">
                    <div className="flex items-center gap-2">
                        <XCircle size={14} className="text-red-600" />
                        <span className="text-xs font-semibold text-red-900">Validation Failed</span>
                    </div>
                    <p className="text-xs text-red-700 mt-1 font-normal">{validationError}</p>
                </div>
            )}

            {/* Store Details Modal */}
            {showDetailedResults && createPortal(
                <StoreDetailsModal
                    store={store}
                    validationResults={validationResults}
                    onClose={() => setShowDetailedResults(false)}
                />,
                document.body
            )}
        </div>
    );
}
